
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Subscription } from "@/types/subscription";

export const useSubscriptionStatus = (currentUserId: string | undefined, profileId: string | undefined) => {
  return useQuery<Subscription | null>({
    queryKey: ["subscription-status", currentUserId, profileId],
    queryFn: async () => {
      if (!currentUserId || !profileId) {
        console.log('Missing required parameters:', { currentUserId, profileId });
        return null;
      }

      try {
        console.log('Checking subscription status with params:', {
          currentUserId,
          profileId,
          origin: window.location.pathname,
          timestamp: new Date().toISOString()
        });

        // Check for active subscription
        const { data: subscription, error } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("subscriber_id", currentUserId)
          .eq("subscribed_to_id", profileId)
          .single();


        if (error) {
          console.error('Subscription query error:', error);
          throw error;
        }

        if (!subscription) {
          console.log('No subscription found');
          return null;
        }

        // For lifetime subscriptions, always return the data
        if (subscription.type === 'lifetime') {
          return subscription as Subscription;
        }

        // For monthly subscriptions, check if it's still within 30 days
        const subscriptionDate = new Date(subscription.created_at);
        const now = new Date();
        const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;

        // If more than 30 days have passed, subscription has expired
        if (now.getTime() - subscriptionDate.getTime() > thirtyDaysInMs) {
          console.log('Subscription expired');
          return null;
        }

        // Return the subscription with its cancelled status
        return subscription as Subscription;
      } catch (error) {
        console.error('Error in subscription status check:', error);
        throw error;
      }
    },
    staleTime: 0,
    refetchInterval: 5000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: !!currentUserId && !!profileId,
  });
};
